/**
 * logger.js — Centralised logging utility for CAI
 *
 * In development : logs to browser console with colour-coded levels.
 * In production  : logs would be sent to a monitoring service
 *                  (e.g. AWS CloudWatch, Sentry — wired up in Phase 5).
 *
 * Usage:
 *   import logger from '../utils/logger';
 *   logger.info('User logged in', { userId: 'u001' });
 *   logger.error('API call failed', error);
 */

// Log levels — controls which messages appear
const LOG_LEVELS = {
  DEBUG: 0,
  INFO:  1,
  WARN:  2,
  ERROR: 3,
};

// In production builds, only show WARN and above
const CURRENT_LEVEL =
  process.env.NODE_ENV === 'production' ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;

// Colour map for console output (development only)
const COLOURS = {
  DEBUG: '#6B7280',
  INFO:  '#3B82F6',
  WARN:  '#F59E0B',
  ERROR: '#EF4444',
};

/**
 * Core log function
 * @param {string} level   - 'DEBUG' | 'INFO' | 'WARN' | 'ERROR'
 * @param {string} message - Human-readable log message
 * @param {*}      data    - Optional extra data (object, error, string)
 */
function log(level, message, data) {
  if (LOG_LEVELS[level] < CURRENT_LEVEL) return;

  const timestamp = new Date().toISOString();
  const prefix    = `[CAI][${timestamp}][${level}]`;

  if (process.env.NODE_ENV !== 'production') {
    // Development: coloured console output
    const style = `color: ${COLOURS[level]}; font-weight: bold;`;
    if (data !== undefined) {
      console.log(`%c${prefix} ${message}`, style, data);
    } else {
      console.log(`%c${prefix} ${message}`, style);
    }
  } else {
    // ─── TODO (Phase 5): Replace with production logging ───────────────────
    // Example: send to AWS CloudWatch Logs or Sentry
    //
    // import * as Sentry from '@sentry/react';
    // if (level === 'ERROR') {
    //   Sentry.captureMessage(message, { level: 'error', extra: { data } });
    // }
    //
    // Or POST to your own logging endpoint:
    // fetch('/api/logs', {
    //   method: 'POST',
    //   body: JSON.stringify({ level, message, data, timestamp }),
    // });
    // ────────────────────────────────────────────────────────────────────────

    // Minimal production fallback — errors still logged
    if (level === 'ERROR') {
      console.error(`${prefix} ${message}`, data || '');
    }
  }
}

const logger = {
  debug: (msg, data) => log('DEBUG', msg, data),
  info:  (msg, data) => log('INFO',  msg, data),
  warn:  (msg, data) => log('WARN',  msg, data),
  error: (msg, data) => log('ERROR', msg, data),
};

export default logger;
